'use client';

import { useCallback, useEffect, useState } from 'react';

import {
	LANDING_PIN_DISMISS_EVENT,
	dismissLandingSpacePin,
	isLandingSpacePinDismissed,
} from '@naumu/shared/spaces/landing-space-pin';

export interface MarketingSpace {
	id: string;
	name: string;
	slug: string;
	icon: string | null;
	color: string | null;
}

export interface MarketingSpaceHint {
	name: string;
	slug: string;
	icon: null;
	color: null;
}

/** Lightweight session shape for public marketing routes - no AuthProvider needed. */
export interface MarketingSession {
	loading: boolean;
	authenticated: boolean;
	isGuest: boolean;
	userName?: string;
	space?: MarketingSpace;
	provisionalSpace?: MarketingSpaceHint;
	/**
	 * Durably unpin the space ("start fresh"): the landing behaves like a first
	 * visit — across reloads and both landing composers — until the user opens
	 * or provisions a space again.
	 */
	dismissSpacePin: () => void;
}

import { isMemberSpace } from '@naumu/shared/spaces/space-membership';
import type { SpaceMembershipKind } from '@naumu/shared/types/project-types';

const LAST_SPACE_KEY = 'naumu-active-project-id';
const LAST_SPACE_COOKIE = 'naumu-last-space';

const readLastSpaceHint = (): MarketingSpaceHint | undefined => {
	if (typeof document === 'undefined') return undefined;
	const prefix = `${LAST_SPACE_COOKIE}=`;
	const value = document.cookie
		.split('; ')
		.find((cookie) => cookie.startsWith(prefix))
		?.slice(prefix.length);
	if (!value) return undefined;

	try {
		const slug = decodeURIComponent(value);
		return { name: slug, slug, icon: null, color: null };
	} catch {
		return undefined;
	}
};

/**
 * Client-only session probe for public marketing routes. The backend's
 * lastSpaceSlug is authoritative, with stale device storage and the first graph
 * as fallbacks. A JS-readable slug cookie supplies an immediate badge hint while
 * the authoritative session and graph list resolve.
 */
export const useMarketingSession = (): MarketingSession => {
	const [info, setInfo] = useState<Omit<MarketingSession, 'dismissSpacePin'>>({
		loading: true,
		authenticated: false,
		isGuest: false,
	});
	// Durable "start fresh" dismissal. Read in an effect (not during render) for
	// the same hydration reason as the cookie hint below; the window event keeps
	// every mounted instance (hero + bottom CTA) in sync within a page-load.
	const [pinDismissed, setPinDismissed] = useState(false);

	useEffect(() => {
		setPinDismissed(isLandingSpacePinDismissed());
		const onDismiss = () => setPinDismissed(true);
		window.addEventListener(LANDING_PIN_DISMISS_EVENT, onDismiss);
		return () => window.removeEventListener(LANDING_PIN_DISMISS_EVENT, onDismiss);
	}, []);

	const dismissSpacePin = useCallback(() => {
		dismissLandingSpacePin();
	}, []);

	// The cookie hint is client-only, so it must NOT be read during render: the
	// server has no cookie access here and would omit the badge while the
	// client's first render drew it, which fails hydration and makes React throw
	// away and re-create the whole tree (including the root layout's inline
	// theme <script>). Applying it in an effect costs one frame and keeps the
	// first client render byte-identical to the SSR output.
	useEffect(() => {
		const hint = readLastSpaceHint();
		if (!hint) return;
		// Only a hint - never clobber the authoritative session once it resolves.
		setInfo((cur) => (cur.loading ? { ...cur, provisionalSpace: hint } : cur));
	}, []);

	useEffect(() => {
		(async () => {
			try {
				const res = await fetch('/api/auth/session', { credentials: 'include' });
				if (!res.ok) {
					setInfo({ loading: false, authenticated: false, isGuest: false });
					return;
				}
				const data = await res.json();
				if (!data?.user) {
					setInfo({ loading: false, authenticated: false, isGuest: false });
					return;
				}

				const graphsRes = await fetch('/api/graphs', { credentials: 'include' });
				const graphEntries: {
					id: string;
					name: string;
					slug?: string;
					icon?: string | null;
					color?: string | null;
					membershipKind?: SpaceMembershipKind;
				}[] = graphsRes.ok ? await graphsRes.json() : [];
				const toMarketingSpace = (graph: (typeof graphEntries)[number]): MarketingSpace => ({
					id: graph.id,
					name: graph.name,
					slug: graph.slug ?? graph.id,
					icon: graph.icon ?? null,
					color: graph.color ?? null,
				});
				const graphs: MarketingSpace[] = graphEntries.map(toMarketingSpace);
				// `/api/graphs` also returns public spaces the user merely pinned
				// ("Keep in rail"). The two MATCHES below are intentional pointers
				// at where the user actually left off, so they may resolve to a
				// pin. The blind fallback may not: for an account with no spaces
				// of its own, `graphs[0]` would silently make a stranger's public
				// space the landing page's "continue in ..." pin.
				const memberGraphs: MarketingSpace[] = graphEntries
					.filter(isMemberSpace)
					.map(toMarketingSpace);

				const backendMatch = data.lastSpaceSlug
					? graphs.find((graph) => graph.slug === data.lastSpaceSlug)
					: undefined;
				const lastId = localStorage.getItem(LAST_SPACE_KEY);
				const storageMatch = lastId
					? graphs.find((graph) => graph.id === lastId)
					: undefined;
				const space = backendMatch ?? storageMatch ?? memberGraphs[0];
				setInfo({
					loading: false,
					authenticated: true,
					isGuest: data.user.isAnonymous === true,
					userName: data.user.name,
					space,
				});
			} catch {
				setInfo({ loading: false, authenticated: false, isGuest: false });
			}
		})();
	}, []);

	// Suppress BOTH the resolved space and the cookie hint while dismissed —
	// the sources themselves (lastSpaceSlug, localStorage, cookie) stay intact,
	// so the pin returns once the dismissal flag is cleared.
	return pinDismissed
		? { ...info, space: undefined, provisionalSpace: undefined, dismissSpacePin }
		: { ...info, dismissSpacePin };
};
